import { useReducer } from 'react'
import moment from 'moment'

import effects from 'app/effects/effects'
import momentTimezone from 'moment-timezone'

import { filterType, filterResultType } from '../constants'
import { dateGeneralFormat } from 'commons/date'

export const ActionType = {
  SET_PLAN_BILL: 'SET_PLAN_BILL',
  SET_ACCUMULATE: 'SET_ACCUMULATE',
  SET_USAGE_LINE_CHART: 'SET_USAGE_LINE_CHART',
  SET_FILTER: 'SET_FILTER',
  SET_SELECTED_DATE: 'SET_SELECTED_DATE',
}

const initialState = {
  planBill: {},
  accumulate: [],
  isLoading: false,
  lineData: {
    total: 0,
    x: [],
    y: [],
  },
  filter: '',
  dateRang: {
    startDate: new Date(),
    endDate: new Date(),
    key: 'selection',
  },
}

const reducer = (state, action) => {
  switch (action.type) {
    case ActionType.SET_PLAN_BILL:
      return {
        ...state,
        planBill: action.payload,
      }
    case ActionType.SET_ACCUMULATE:
      return {
        ...state,
        accumulate: action.payload,
      }
    case ActionType.SET_USAGE_LINE_CHART:
      return {
        ...state,
        lineData: action.payload,
        isLoading: false,
      }
    case ActionType.SET_FILTER:
      return {
        ...state,
        filter: action.key,
        dateRang: {
          startDate: new Date(),
          endDate: new Date(),
          key: 'selection',
        },
        lineData: {
          total: 0,
          x: [],
          y: [],
        },
        accumulate: [],
        isLoading: true,
      }
    case ActionType.SET_SELECTED_DATE:
      return {
        ...state,
        filter: null,
        dateRang: action.date,
        lineData: {
          total: 0,
          x: [],
          y: [],
        },
        accumulate: [],
        isLoading: true,
      }
    case ActionType.SET_DATE:
      return {
        ...state,
        startDate: new Date(action.startDate),
        endDate: new Date(action.endDate),
      }
    default:
      return state
  }
}

export default function useUsageSummary() {
  const [state, dispatch] = useReducer(reducer, initialState)

  const handleSubtract = (count, key) => {
    return moment()
      .subtract(count, key)
      .startOf('day')
      .format(dateGeneralFormat)
  }
  const endDay = moment().endOf('day').format(dateGeneralFormat)

  const fetchBillingPlan = () => {
    effects.apis.getBillingPlan().then((response) => {
      dispatch({ type: 'SET_PLAN_BILL', payload: response })
    })
  }

  const fetchBillingAccumulateUsage = (startEndDate) => {
    effects.apis
      .getBillingAccumulateUsage({
        ...startEndDate,
        showAll: false,
        type: filterResultType.DAYS,
        timezone: momentTimezone.tz.guess(true),
      })
      .then((response) => {
        dispatch({ type: 'SET_ACCUMULATE', payload: response })
      })
  }

  const fetchUsageSummaryLineChart = (startEndDate) => {
    effects.apis
      .getUsageLineChart({
        ...startEndDate,
        type: filterResultType.DAYS,
        timezone: momentTimezone.tz.guess(true),
      })
      .then((response) => {
        dispatch({ type: 'SET_USAGE_LINE_CHART', payload: response })
      })
  }

  const handleStartEndDate = (key) => {
    switch (key) {
      case filterType.ALL:
        return { startDate: '', endDate: '' }
      case filterType.TODAY:
        return {
          startDate: moment().startOf('day').format(dateGeneralFormat),
          endDate: endDay,
        }
      case filterType.LAST_SEVEN_DAYS:
        return {
          startDate: handleSubtract(7, 'days'),
          endDate: endDay,
        }
      case filterType.LAST_THIRTY_DAYS:
        return {
          startDate: handleSubtract(30, 'days'),
          endDate: endDay,
        }
      case filterType.LAST_SIX_MONTHS:
        return {
          startDate: handleSubtract(6, 'months'),
          endDate: endDay,
        }
      case filterType.LAST_YEAR:
        return {
          startDate: handleSubtract(1, 'years'),
          endDate: endDay,
        }
      default:
        return { startDate: '', endDate: '' }
    }
  }

  const handleFirstMount = async () => {
    const rangDate = handleStartEndDate(filterType.LAST_THIRTY_DAYS)
    dispatch({
      type: 'SET_FILTER',
      key: filterType.LAST_THIRTY_DAYS,
    })

    await fetchBillingPlan()
    await fetchBillingAccumulateUsage(rangDate)
    await fetchUsageSummaryLineChart(rangDate)
  }

  const handleSelectedFilter = async (key) => {
    const rangDate = handleStartEndDate(key)
    dispatch({ type: 'SET_FILTER', key: key })

    await fetchBillingAccumulateUsage(rangDate)
    await fetchUsageSummaryLineChart(rangDate)
  }

  const handleSelectedDate = async (date) => {
    const rangDate = {
      startDate: moment(date.startDate)
        .startOf('day')
        .format(dateGeneralFormat),
      endDate: moment(date.endDate).endOf('day').format(dateGeneralFormat),
    }
    dispatch({ type: 'SET_SELECTED_DATE', date: date })

    await fetchBillingAccumulateUsage(rangDate)
    await fetchUsageSummaryLineChart(rangDate)
  }
  return {
    state,
    handleFirstMount,
    handleSelectedFilter,
    handleSelectedDate,
  }
}
